import React, { FC, useEffect, useRef } from "react";
import styled from "@emotion/styled";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: Record<string, unknown>
      ) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise: Promise<void> | null = null;
const loadTurnstile = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Failed to load Turnstile"));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

export interface TurnstileProps {
  sitekey: string;
  error?: string;
  theme?: "light" | "dark";
  onChange: (token: string) => void;
}

const Turnstile: FC<TurnstileProps> = ({
  sitekey,
  error,
  theme = "light",
  onChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let widgetId: string | undefined;
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey,
          theme,
          callback: (token: string) => onChange(token),
          "expired-callback": () => onChange(""),
          "error-callback": () => onChange(""),
        });
      })
      .catch(() => {
        // leave the widget empty; form validation will block submission
      });

    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [sitekey, theme]);

  return (
    <TurnstileWrapper>
      <div ref={containerRef} />
      {error && <TurnstileError>{error}</TurnstileError>}
    </TurnstileWrapper>
  );
};

const TurnstileWrapper = styled.div`
  display: block;
`;

const TurnstileError = styled.span`
  display: block;
  color: #e31c79;
  margin-top: 0.1rem;
  font-family: "Clan news", sans-serif;
`;

export default Turnstile;
